import { Negotiation } from '@interfaces/models/negotiation';
import { CartContext } from '@context/cart.context';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import sanitizer from '@helpers/sanitizer';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { Product } from '@interfaces/models/product';
import { useContextSelector } from 'use-context-selector';
import logger from '@helpers/utils/logger/client';
import clsx from 'clsx';
import styles from '../CTA-product.module.scss';

export interface CTAMMAOBlockProps {
  showBuyNow?: boolean;
  applyTopMargin?: boolean;
  setLoading?: (e) => void;
  negotiation?: Negotiation;
  onClose?: () => void;
  trackingFrom: string;
  showMMAOBanner: boolean | unknown;
  product: Product;
}

const CTAMMAOBlock: React.FC<CTAMMAOBlockProps> = (props) => {
  const { trackingFrom, negotiation, showBuyNow, applyTopMargin, setLoading, onClose, showMMAOBanner, product } = props;

  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const { sendAnalyticEvent: sendMMAOAnalyticEvents } = useAnalyticEvents('mmao');
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const { t } = useTranslation();

  const addToCart = async (): Promise<void> => {
    try {
      onClose?.();
      await addToShoppingCart(product, false);
    } catch (e) {
      logger.error(e, 'Error adding to cart - MMAO-modal');
    }
  };

  const buyersWhoHaveActiveNegotiation = product?.buyersWhoHaveActiveNegotiation || [];

  const displayBuyNowButton = useMemo<boolean>(() => {
    return showBuyNow && !isProductIdInCart(productsInCart, product?.id) && !negotiation?.isAccepted;
  }, [productsInCart]);

  const handleBuyNow = async () => {
    // Track: This event gets triggered when the user clicks on the buy now in the negotiating area
    sendMMAOAnalyticEvents('mmao_buy_now');

    // Add to cart when user clicks on `Buy Now` CTA
    try {
      setLoading(true);
      await addToCart();
    } catch (e) {
      setLoading(false);
      logger.error(e, 'Error adding to cart - CTA-buttons-block');
    }
  };

  /* TODO: Need to find better solution, instead of this ugly workaround :(
      - 'mmao_ongoing_banner_pdp' event is getting called before 'PageView' event
      - Due to this overlap this particular event is not getting logged on kibana
      - window?.tc_events_7 is still 'undefined' while this event is being sent to snowplow
  */
  // @ts-ignore
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const tcEventLoaded = typeof window !== 'undefined' && typeof window?.tc_events_7 === 'function';

  useEffect(() => {
    // Track: When the negotiating area page is loaded with the banner either on MMAO popup or PDP
    if (showMMAOBanner && tcEventLoaded) {
      sendMMAOAnalyticEvents(trackingFrom, {
        eventValue: buyersWhoHaveActiveNegotiation?.length?.toString(),
        eventProperty: negotiation?.product?.id,
      });
    }
  }, [showMMAOBanner, trackingFrom, tcEventLoaded, negotiation]);

  return (
    <>
      {showMMAOBanner && (
        <div className={clsx(styles.mmao__offers, applyTopMargin && styles['mmao__offers--topMargin'])}>
          {buyersWhoHaveActiveNegotiation?.length > 1 ? (
            <p className={styles.mmao__offers__text}>
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(
                    t('MMAO.ONGOING_BANNER_PROGRESS_OFFER_PLURAL', {
                      offerCount: buyersWhoHaveActiveNegotiation.length,
                    }),
                  ),
                }}
              />
            </p>
          ) : (
            <p className={styles.mmao__offers__text}>
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizer(t('MMAO.ONGOING_BANNER_PROGRESS_OFFER_SINGLE')),
                }}
              />
            </p>
          )}
          {displayBuyNowButton && (
            <div>
              <button
                className="vc-btn vc-btn--primary vc-btn--small"
                onClick={handleBuyNow}
              >
                {t('MMAO.ONGOING_BANNER_OFFER_BUY_NOW')}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default CTAMMAOBlock;
